import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { LifeBuoy, Printer, Pencil, Plus, X, Share2 } from "lucide-react";
import api from "../../api";
import LineListInput from "../LineListInput";
import { printSafetyPlan, SAFETY_PLAN_STEPS } from "../../utils/safetyPlanPrinter";

// Safety plan builder on the patient record's Risk & safety tab (backend
// clinical/safety/safety_plan_routes.py). Made together with the patient; every
// save is a new version. "Share with the patient" puts it in their app's
// My Care.
const blank = () => ({ warning_signs: [], coping_strategies: [], distractions: [], people_to_ask: [], professionals: [], means_safety: [], reasons_for_living: [], shared_with_patient: true });

function ContactsEditor({ value, onChange, namePlaceholder }) {
  const rows = value.length ? value : [{ name: "", phone: "" }];
  const set = (i, key, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2">
          <input value={r.name} onChange={(e) => set(i, "name", e.target.value)} placeholder={namePlaceholder} aria-label="Name"
            className="flex-1 min-w-0 rounded-md border border-secondary-300 p-2 text-sm" />
          <input value={r.phone} onChange={(e) => set(i, "phone", e.target.value)} placeholder="Phone" aria-label="Phone" inputMode="tel"
            className="w-36 rounded-md border border-secondary-300 p-2 text-sm" />
          <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label="Remove" className="p-2 text-secondary-400 hover:text-red-600"><X className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...rows, { name: "", phone: "" }])} className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600"><Plus className="w-3.5 h-3.5" /> Add another</button>
    </div>
  );
}

export default function SafetyPlanSection({ patientId, patientName, riskLevel, onSaved }) {
  const [plan, setPlan] = useState(undefined); // undefined = loading, null = none yet
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getSafetyPlan(patientId).then((r) => setPlan(r.data)).catch(() => setPlan(null));
  }, [patientId]);

  const startEdit = () => setDraft(plan ? { ...blank(), ...plan } : blank());
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...draft };
      ["people_to_ask", "professionals"].forEach((k) => { body[k] = (body[k] || []).filter((c) => (c.name || "").trim() || (c.phone || "").trim()); });
      const r = await api.saveSafetyPlan(patientId, body);
      setPlan(r.data);
      setDraft(null);
      toast.success(r.data.shared_with_patient ? "Safety plan saved and shared with the patient" : "Safety plan saved");
      if (onSaved) onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't save the safety plan.");
    } finally {
      setSaving(false);
    }
  };

  const print = async () => {
    let header = {};
    try { header = (await api.getOrgConfig("prescription_header")).data?.config_data || {}; } catch { /* print without letterhead */ }
    if (!printSafetyPlan({ plan, patientName, header })) toast.error("Allow pop-ups to print the safety plan.");
  };

  if (plan === undefined) return <div className="rounded-xl bg-white border border-secondary-100 p-4 text-sm text-secondary-500">Loading safety plan…</div>;

  if (draft) {
    return (
      <div className="rounded-xl bg-white border border-secondary-100 p-4 md:p-5 space-y-4">
        <div className="flex items-center gap-2">
          <LifeBuoy className="w-5 h-5 text-rose-600" />
          <h3 className="text-base font-bold text-secondary-900">{plan ? `Update safety plan (version ${plan.version + 1})` : "New safety plan"}</h3>
        </div>
        <p className="text-sm text-secondary-600">Fill this in together with {patientName || "the patient"}, in their own words.</p>
        {SAFETY_PLAN_STEPS.filter((s) => !s.lines).map((s) => (
          <div key={s.key}>
            <p className="text-sm font-semibold text-secondary-800">{s.title}</p>
            {s.hint && <p className="text-xs text-secondary-500 mb-1">{s.hint}{!s.contacts && " - one per line"}</p>}
            {s.contacts
              ? <ContactsEditor value={draft[s.key] || []} onChange={(v) => setDraft({ ...draft, [s.key]: v })}
                  namePlaceholder={s.key === "professionals" ? "e.g. Dr. Raj Thakkar (my psychologist)" : "e.g. Priya (sister)"} />
              : <LineListInput value={draft[s.key] || []} onChange={(v) => setDraft({ ...draft, [s.key]: v })} rows={3} />}
          </div>
        ))}
        <p className="text-xs text-secondary-500">Tele-MANAS 14416 and 112 are added to every plan automatically.</p>
        <label className="normal-case font-sans tracking-normal flex items-start gap-2 text-sm text-secondary-700 cursor-pointer">
          <input type="checkbox" className="mt-1 h-4 w-4" checked={!!draft.shared_with_patient} onChange={(e) => setDraft({ ...draft, shared_with_patient: e.target.checked })} />
          <span>Share with the patient - it appears in "My Care" in their booking app and keeps working without internet once opened.</span>
        </label>
        <div className="flex gap-2">
          <button type="button" onClick={save} disabled={saving} className="rounded-lg bg-primary-600 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">{saving ? "Saving…" : "Save safety plan"}</button>
          <button type="button" onClick={() => setDraft(null)} className="rounded-lg border border-secondary-300 text-sm px-4 py-2">Cancel</button>
        </div>
      </div>
    );
  }

  if (!plan) {
    const urgent = riskLevel === "moderate" || riskLevel === "high";
    return (
      <div className={`rounded-xl border p-4 flex flex-wrap items-center justify-between gap-3 ${urgent ? "border-rose-300 bg-rose-50" : "border-secondary-100 bg-white"}`}>
        <div className="flex items-start gap-2">
          <LifeBuoy className={`w-5 h-5 mt-0.5 ${urgent ? "text-rose-600" : "text-secondary-400"}`} />
          <div>
            <p className="text-sm font-semibold text-secondary-900">No safety plan yet</p>
            <p className="text-sm text-secondary-600">{urgent ? `${patientName || "This patient"} is at ${riskLevel} risk. Make a safety plan with them.` : "A short plan the patient can turn to when things get hard."}</p>
          </div>
        </div>
        <button type="button" onClick={startEdit} className="rounded-lg bg-primary-600 text-white text-sm font-semibold px-4 py-2">Make a safety plan</button>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-white border border-secondary-100 p-4 md:p-5 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <LifeBuoy className="w-5 h-5 text-rose-600" />
        <h3 className="text-base font-bold text-secondary-900">Safety plan</h3>
        <span className="text-xs text-secondary-500">version {plan.version} · {new Date(plan.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}{plan.created_by_name ? ` · ${plan.created_by_name}` : ""}</span>
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${plan.shared_with_patient ? "bg-emerald-100 text-emerald-800" : "bg-secondary-100 text-secondary-600"}`}>
          <Share2 className="w-3 h-3" /> {plan.shared_with_patient ? "Shared with patient" : "Not shared"}
        </span>
        <span className="ml-auto flex gap-2">
          <button type="button" onClick={print} className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 text-xs font-semibold px-3 py-1.5"><Printer className="w-3.5 h-3.5" /> Print</button>
          <button type="button" onClick={startEdit} className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 text-white text-xs font-semibold px-3 py-1.5"><Pencil className="w-3.5 h-3.5" /> Update</button>
        </span>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {SAFETY_PLAN_STEPS.map((s) => {
          const v = plan[s.key] || [];
          if (!v.length) return null;
          return (
            <div key={s.key} className="rounded-lg bg-secondary-50 p-3">
              <p className="text-xs font-bold text-secondary-700 mb-1">{s.title}</p>
              <ul className="list-disc pl-4 text-sm text-secondary-800 space-y-0.5">
                {v.map((item, i) => (
                  <li key={i}>{s.contacts ? <>{item.name}{item.phone ? ` · ${item.phone}` : ""}</> : s.lines ? <>{item.number}{item.alt ? ` or ${item.alt}` : ""} · {item.name}</> : item}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
